import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtOrEmpty } from "@/lib/format";
import { RapItemDialog } from "@/components/RapItemDialog";
import { toast } from "sonner";
import { Loader2, Plus, Check, ShieldCheck } from "lucide-react";

const ST_CLS = { "Belum Dipesan": "bg-slate-100 text-slate-700", Dipesan: "bg-amber-100 text-amber-800", Datang: "bg-sky-100 text-sky-800",
  Terpasang: "bg-emerald-100 text-emerald-800", Rencana: "bg-slate-100 text-slate-700", Berjalan: "bg-amber-100 text-amber-800", Selesai: "bg-emerald-100 text-emerald-800" };
const Pill = ({ v, cls }) => <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${cls || ST_CLS[v] || "bg-slate-100"}`}>{v}</span>;

function SumBox({ label, value, testid, strong }) {
  return (
    <div className={`rounded-md border p-3 ${strong ? "border-primary bg-red-50" : ""}`}>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`font-extrabold ${strong ? "text-primary text-lg" : "text-navy text-base"} ${value === "Belum Diinput" ? "!text-sm !text-slate-500" : ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function AddForm({ kind, pid, onAdded }) {
  const [f, setF] = useState({ name: "", code: "", qty_plan: "", unit: kind === "sparepart" ? "pcs" : "paket" });
  const add = async () => {
    try {
      await api.post(`/rap/${pid}/items`, { kind, name: f.name, code: f.code, qty_plan: Number(f.qty_plan), unit: f.unit });
      toast.success("Item ditambahkan"); setF({ ...f, name: "", code: "", qty_plan: "" }); onAdded();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid={`rap-add-${kind}`}>
      <Input className="col-span-2" placeholder={kind === "sparepart" ? "Nama sparepart" : "Jenis jasa / sublet"} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`rap-add-${kind}-name`} />
      {kind === "sparepart" ? <Input placeholder="Kode" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="rap-add-sparepart-code" /> : <span className="hidden sm:block" />}
      <Input type="number" min="0" placeholder="Jumlah" value={f.qty_plan} onChange={(e) => setF({ ...f, qty_plan: e.target.value })} data-testid={`rap-add-${kind}-qty`} />
      <div className="flex gap-2"><Input placeholder="Satuan" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid={`rap-add-${kind}-unit`} />
        <Button variant="outline" disabled={!f.name.trim() || !f.qty_plan} onClick={add} data-testid={`rap-add-${kind}-btn`}><Plus size={14} /></Button></div>
    </div>
  );
}

function ItemTable({ rows, kind, rap, onEdit, onAction }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground py-4 text-center">Belum ada item.</p>;
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-xs min-w-[720px]">
        <thead className="bg-slate-50 text-muted-foreground"><tr className="text-left">
          <th className="p-2">Item</th><th className="p-2 text-right">Kebutuhan</th>
          {kind === "sparepart" && <><th className="p-2 text-right">Diterima</th><th className="p-2 text-right">Terpasang</th></>}
          <th className="p-2 text-right">Total Rencana</th><th className="p-2 text-right">Total Aktual</th><th className="p-2">Status</th><th className="p-2"></th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t align-top" data-testid={`rap-row-${r.id}`}>
              <td className="p-2"><div className="font-semibold text-navy">{r.name}</div><div className="text-[10px] text-muted-foreground">{[r.code, r.supplier, r.spec].filter(Boolean).join(" · ") || "-"}</div></td>
              <td className="p-2 text-right whitespace-nowrap">{r.qty_plan} {r.unit}</td>
              {kind === "sparepart" && <><td className="p-2 text-right">{r.qty_received}</td><td className="p-2 text-right">{r.qty_installed}</td></>}
              <td className="p-2 text-right font-bold whitespace-nowrap" data-testid={`rap-plan-${r.id}`}>{r.plan_price === null && r.editable.includes("plan_price") === false && r.price_status !== "Menunggu Penetapan Harga" ? "Tersembunyi" : r.price_status === "Disetujui" ? fmtOrEmpty(r.plan_total, "Tersembunyi") : <Pill v={r.price_status} cls="bg-amber-100 text-amber-800" />}</td>
              <td className="p-2 text-right font-bold whitespace-nowrap" data-testid={`rap-actual-${r.id}`}>{r.actual_status === "Terverifikasi" ? fmtOrEmpty(r.actual_total_calc, "Tersembunyi") : <Pill v={r.actual_status} cls={r.actual_status === "Belum Diinput" ? "bg-slate-100 text-slate-600" : "bg-sky-100 text-sky-800"} />}</td>
              <td className="p-2"><Pill v={r.status} /></td>
              <td className="p-2 whitespace-nowrap space-x-1">
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onEdit(r)} data-testid={`rap-edit-${r.id}`}>Ubah</Button>
                {rap.can_approve && r.price_status === "Draft" && <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onAction(r, "approve-price")} data-testid={`rap-approve-${r.id}`}><Check size={13} /></Button>}
                {rap.can_verify && r.actual_status === "Menunggu Verifikasi" && <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => onAction(r, "verify-actual")} data-testid={`rap-verify-${r.id}`}><ShieldCheck size={13} /></Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const RapPanel = ({ projectId }) => {
  const [rap, setRap] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/rap/${projectId}`).then(({ data }) => setRap(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), [projectId]);
  useEffect(() => { load(); }, [load]);
  const action = async (r, a) => {
    try { await api.post(`/rap/items/${r.id}/${a}`, { reason: "" }); toast.success(a === "approve-price" ? "Harga disetujui" : "Aktual terverifikasi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!rap) return <Card className="p-6 flex justify-center"><Loader2 className="animate-spin text-primary" /></Card>;
  const s = rap.summary;
  const v = (x) => fmtOrEmpty(x, "Belum Diinput");
  return (
    <div className="space-y-3" data-testid="rap-panel">
      {s && (
        <Card className="p-4" data-testid="rap-summary">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Rekap Biaya RAP</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <SumBox label="Bahan & Material (Rencana / Aktual)" value={`${v(s.material_plan)} / ${v(s.material_actual_total)}`} testid="rap-sum-material" />
            <SumBox label="Sparepart (Rencana / Aktual)" value={`${v(s.sparepart_plan)} / ${v(s.sparepart_actual)}`} testid="rap-sum-sparepart" />
            <SumBox label="Jasa/Sublet (Rencana / Aktual)" value={`${v(s.service_plan)} / ${v(s.service_actual)}`} testid="rap-sum-service" />
            <SumBox label="Total RAP Rencana" value={v(s.rap_plan)} testid="rap-sum-plan" strong />
            <SumBox label="Total Biaya Aktual" value={v(s.hpp_actual)} testid="rap-sum-actual" strong />
            <SumBox label="Selisih Rencana vs Aktual" value={v(s.variance)} testid="rap-sum-variance" />
          </div>
          {(s.rap_pending_price > 0 || s.rap_pending_actual > 0) && <p className="text-[11px] text-amber-700 mt-2" data-testid="rap-pending-note">{s.rap_pending_price} item menunggu penetapan/persetujuan harga · {s.rap_pending_actual} item aktual belum diinput/diverifikasi (tidak dihitung).</p>}
        </Card>
      )}
      <Card className="p-3">
        <Tabs defaultValue="sparepart">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="material" data-testid="rap-tab-material" className="text-xs">Bahan & Material</TabsTrigger>
            <TabsTrigger value="sparepart" data-testid="rap-tab-sparepart" className="text-xs">Sparepart</TabsTrigger>
            <TabsTrigger value="service" data-testid="rap-tab-service" className="text-xs">Jasa/Sublet</TabsTrigger>
          </TabsList>
          <TabsContent value="material" className="mt-3">
            {!rap.material ? <p className="text-sm text-muted-foreground">Tidak tersedia untuk role Anda.</p> : (
              <>
                <p className="text-[11px] text-muted-foreground mb-2">Rencana dari RAB Bahan, aktual dari Material Sheet (tidak dicatat ulang).{" "}
                  {rap.material.rab_id && <Link className="text-primary underline" to={`/rab/${rap.material.rab_id}`} data-testid="rap-open-rab">Buka RAB</Link>}</p>
                {rap.material.items.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">RAB bahan: BELUM TERISI.</p> : (
                  <div className="overflow-x-auto"><table className="w-full text-xs min-w-[480px]"><thead className="bg-slate-50 text-muted-foreground"><tr className="text-left"><th className="p-2">Bahan</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Harga</th><th className="p-2 text-right">Total Rencana</th></tr></thead>
                    <tbody>{rap.material.items.map((m, i) => <tr key={i} className="border-t"><td className="p-2">{m.name}</td><td className="p-2 text-right">{m.qty_plan} {m.unit}</td><td className="p-2 text-right">{fmtOrEmpty(m.price_plan, "Tersembunyi")}</td><td className="p-2 text-right font-bold">{fmtOrEmpty(m.total_plan, "Tersembunyi")}</td></tr>)}</tbody></table></div>
                )}
              </>
            )}
          </TabsContent>
          {["sparepart", "service"].map((k) => (
            <TabsContent key={k} value={k} className="mt-3">
              {rap.can_create.includes(k) && <AddForm kind={k} pid={projectId} onAdded={load} />}
              <ItemTable rows={k === "sparepart" ? rap.spareparts : rap.services} kind={k} rap={rap} onEdit={setEdit} onAction={action} />
            </TabsContent>
          ))}
        </Tabs>
      </Card>
      <RapItemDialog item={edit} kind={edit?.kind} statuses={edit?.kind === "sparepart" ? rap.sp_statuses : rap.sv_statuses} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
};
